import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Stack,
  LinearProgress,
  useTheme,
  Backdrop,
  Card,
  CardContent,
  IconButton,
  Fade,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import Grid2 from '@mui/material/Grid2';
import RocketIcon from '@mui/icons-material/RocketLaunch';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import CreateApplication from '../developer/CreateApplication';
import WelcomeBanner from '../../components/WelcomeBanner';
import ActionCards from '../../components/ActionCards';
import LineChart from '../../components/graphs/LineChart';
import MetricCard from '../../components/graphs/MetricCard';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { httpService } from '../../services/httpService';
import { decryptResponse, encryptResponse, getSessionKey, REQ_ENC_KEY } from '../../services';

interface CardItem {
  title: string;
  desc: string;
  action: string;
  icon: React.ReactElement;
}

interface ChartDataItem {
  name: string;
  users: number;
}

interface TourStep {
  title: string;
  description: string;
  target: string;
}

interface AnalyticsData {
  total_participants: {
    total_count: number;
  };
  total_groups: {
    total_count: number;
  };
  participants_by_month: {
    chart_data: Array<{
      month: string;
      year: number;
      month_number: number;
      count: number;
      month_name: string;
      display_name: string;
    }>;
  };
}

export default function Home(): React.ReactElement {
  const theme = useTheme();
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [showTour, setShowTour] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const applicationDetails = useSelector((state: any) => state.applicationDetails);
  const organizationId = userDetails?.organization_id;
  const appId = applicationDetails?.app_id;

  useEffect(() => {
    if (organizationId && appId) {
      fetchData(organizationId, appId);
    }
  }, [appId, organizationId]);

  const fetchData = async (organizationId: string, appId: string) => {
    try {
      setLoading(true);
      const response = await httpService.post<any>('/analytics/getCommonAnalyticsData', {
        data: await encryptResponse({
          organization_id: organizationId,
          app_id: appId,
          user_id: userDetails?.user_id || '',
        }, REQ_ENC_KEY)
      });
      if (response.data && (response.data as any).data) {
        const data = response.data.data;
        const decrypted = await decryptResponse<any>(data, getSessionKey() as string);
        const { result } = decrypted;
        setAnalyticsData(result);
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      setAnalyticsData(null);
    } finally {
      setLoading(false);
    }
  };

  const tourSteps: TourStep[] = [
    {
      title: "Create Application",
      description: "Start by creating your first WebRTC application. Click this card to open the application builder.",
      target: "create-app-card"
    },
    {
      title: "Setup Keyset",
      description: "Configure your API keys and authentication settings for secure communication.",
      target: "setup-keyset-card"
    },
    {
      title: "Add Participants",
      description: "Invite team members to collaborate on your projects and manage permissions.",
      target: "add-participants-card"
    }
  ];

  useEffect(() => {
    if (!userDetails.is_first_app_created) {
      console.log('Starting tour for new user', userDetails);
      setShowTour(true);
    }
  }, [userDetails]);


  const handleCloseCreateDialog = (): void => {
    setCreateDialogOpen(false);
  };

  const handleGetStarted = (): void => {
    setCreateDialogOpen(true);
  };

  const handleClick = (action: string): void => {
    switch (action) {
      case "LET'S GO":
        setCreateDialogOpen(true);
        break;
      case 'SETUP':
        navigate('/api-keys');
        break;
      case 'ADD PARTICIPANTS':
        navigate('/application-participants');
        console.log('Add participants clicked');
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleNextStep = (): void => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowTour(false);
      localStorage.setItem('sampark_tour_completed', 'true');
    }
  };

  const handleSkipTour = (): void => {
    setShowTour(false);
    localStorage.setItem('sampark_tour_completed', 'true');
  };

  const cards: CardItem[] = [
    {
      title: 'Create Application',
      desc: 'Build your first application',
      action: "LET'S GO",
      icon: <RocketIcon sx={{ fontSize: 28 }} />,
    },
    {
      title: 'Setup Keyset',
      desc: 'Create your API keys',
      action: 'SETUP',
      icon: <VpnKeyIcon sx={{ fontSize: 28 }} />,
    },
    {
      title: 'Add Participants',
      desc: 'Invite your team to collaborate',
      action: 'ADD PARTICIPANTS',
      icon: <PersonAddAlt1Icon sx={{ fontSize: 28 }} />,
    },
  ];

  // Transform analytics data for the line chart
  const chartData = analyticsData?.participants_by_month?.chart_data?.map(item => ({
    name: item.month_name,
    participants: item.count,
    display_name: item.display_name
  })) || [];

  const metricCardsData = [
    {
      title: 'Total Users',
      value: analyticsData?.total_participants?.total_count || 0,
      icon: <PersonAddAlt1Icon />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      accentColor: '#667eea',
      change: '+12% from last month',
      changeType: 'positive' as const
    },
    {
      title: 'Total Groups',
      value: analyticsData?.total_groups?.total_count || 0,
      icon: <VpnKeyIcon />,
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      accentColor: '#f093fb',
      change: '+8% from last month',
      changeType: 'positive' as const
    }
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 2 } }}>
      {showTour && (
        <Backdrop
          open={showTour}
          sx={{
            zIndex: theme.zIndex.modal,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: '200px',
              left: currentStep === 0 ? '30%' : currentStep === 1 ? '60%' : '75%',
              transform: 'translateX(-50%)',
              zIndex: theme.zIndex.modal + 2,
              animation: 'bounce 2s infinite',
              '@keyframes bounce': {
                '0%, 20%, 50%, 80%, 100%': {
                  transform: 'translateX(-50%) translateY(0)',
                },
                '40%': {
                  transform: 'translateX(-50%) translateY(-20px)',
                },
                '60%': {
                  transform: 'translateX(-50%) translateY(-10px)',
                },
              },
            }}
          >
            <Fade in={showTour} timeout={800}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.warning.main,
                    fontWeight: 'bold',
                    mb: 1,
                    textAlign: 'center',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    px: 2,
                    py: 0.5,
                    borderRadius: 2,
                    fontSize: '0.85rem',
                  }}
                >
                  {currentStep + 1}. {tourSteps[currentStep].title}
                </Typography>
                <ArrowDownwardIcon
                  sx={{
                    fontSize: 40,
                    color: theme.palette.warning.main,
                    animation: 'pulse 1.5s infinite',
                    '@keyframes pulse': {
                      '0%': {
                        opacity: 1,
                        transform: 'scale(1.1)',
                      },
                      '50%': {
                        opacity: 0.7,
                        transform: 'scale(1.1)',
                      },
                      '100%': {
                        opacity: 1,
                        transform: 'scale(1)',
                      },
                    },
                  }}
                />
              </Box>
            </Fade>
          </Box>

          {/* Tour Guide Card */}
          <Card
            sx={{
              position: 'fixed',
              top: '20px',
              right: '20px',
              width: '380px',
              zIndex: theme.zIndex.modal + 1,
              borderRadius: 3,
              boxShadow: theme.shadows[20],
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                  <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
                    🚀 {tourSteps[currentStep].title}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Step {currentStep + 1} of {tourSteps.length}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleSkipTour}
                  sx={{
                    mt: -1,
                    mr: -1,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>

              <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.6, opacity: 0.9 }}>
                {tourSteps[currentStep].description}
              </Typography>

              {/* Progress Bar */}
              <Box sx={{ mb: 3 }}>
                <LinearProgress
                  variant="determinate"
                  value={((currentStep + 1) / tourSteps.length) * 100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: theme.palette.warning.main,
                      borderRadius: 3,
                    }
                  }}
                />
              </Box>

              <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
                <Button
                  size="small"
                  onClick={handleSkipTour}
                  sx={{
                    color: 'rgba(255,255,255,0.7)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    }
                  }}
                >
                  Skip Tour
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleNextStep}
                  sx={{
                    backgroundColor: theme.palette.warning.main,
                    color: 'white',
                    fontWeight: 'bold',
                    px: 3,
                    '&:hover': {
                      backgroundColor: theme.palette.warning.dark,
                    }
                  }}
                >
                  {currentStep === tourSteps.length - 1 ? '🎉 Finish' : 'Next →'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Backdrop>
      )}

      <WelcomeBanner onGetStarted={handleGetStarted} />

      {/* Action Cards Component */}
      <ActionCards
        cards={cards}
        onCardClick={handleClick}
        showTour={showTour}
        currentTourTarget={showTour ? tourSteps[currentStep].target : ''}
      />

      {/* Analytics Metrics */}
      <Box mt={6}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
          Analytics Overview
        </Typography>
        <Grid2 container spacing={3} sx={{ mb: 4 }}>
          {metricCardsData.map((metric, index) => (
            <Grid2 key={index} size={{ xs: 12, sm: 6, md: 6 }}>
              <MetricCard data={metric} />
            </Grid2>
          ))}
        </Grid2>
      </Box>

      {/* Participants Chart */}
      {loading ? (
        <Box mt={4}>
          <Paper elevation={6} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Loading analytics data...
            </Typography>
            <LinearProgress />
          </Paper>
        </Box>
      ) : (
        <Box mt={4}>
          <LineChart
            title="Users Added by Month"
            description='Number of users added each month'
            data={chartData}
            dataKeys={['participants']}
            xAxisKey="name"
            height={500}
            graphHeight={380}
            xAxisTitle="Month"
            yAxisTitle="Number of Users"
          />
        </Box>
      )}
      {createDialogOpen && (
        <CreateApplication
          open={createDialogOpen}
          onClose={handleCloseCreateDialog}
        />
      )}
    </Box>
  );
}
